// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useState } from 'react';

interface AuthConfig {
  recaptchaEnabled: boolean;
  recaptchaSiteKey: string;
  googleAuthEnabled: boolean;
  githubAuthEnabled: boolean;
  gdriveEnabled: boolean;
  dropboxEnabled: boolean;
  userPromptsEnabled: boolean;
}

const defaultConfig: AuthConfig = {
  recaptchaEnabled: false,
  recaptchaSiteKey: '',
  googleAuthEnabled: false,
  githubAuthEnabled: false,
  gdriveEnabled: false,
  dropboxEnabled: false,
  userPromptsEnabled: true,
};

// Module-level cache so we only fetch once per page load.
let cachedConfig: AuthConfig | null = null;
let fetchPromise: Promise<AuthConfig> | null = null;

async function fetchAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return cachedConfig;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const resp = await fetch('/api/v1/auth/config');
      if (!resp.ok) return defaultConfig;

      const json = await resp.json();
      cachedConfig = {
        recaptchaEnabled: json.data?.recaptcha_enabled ?? false,
        recaptchaSiteKey: json.data?.recaptcha_site_key ?? '',
        googleAuthEnabled: json.data?.google_auth_enabled ?? false,
        githubAuthEnabled: json.data?.github_auth_enabled ?? false,
        gdriveEnabled: json.data?.gdrive_enabled ?? false,
        dropboxEnabled: json.data?.dropbox_enabled ?? false,
        userPromptsEnabled: json.data?.user_prompts_enabled ?? true,
      };
      return cachedConfig;
    } catch {
      return defaultConfig;
    }
  })();

  return fetchPromise;
}

/**
 * Hook that fetches the public auth configuration (reCAPTCHA settings,
 * OAuth provider availability, storage provider availability).
 * The config is fetched once and cached for the lifetime of the page.
 *
 * Uses plain fetch instead of the API client because this endpoint is
 * unauthenticated and must work before the user logs in.
 */
export function useAuthConfig() {
  const [config, setConfig] = useState<AuthConfig>(cachedConfig ?? defaultConfig);
  const [isLoading, setIsLoading] = useState(!cachedConfig);

  useEffect(() => {
    if (cachedConfig) {
      setConfig(cachedConfig);
      setIsLoading(false);
      return;
    }

    fetchAuthConfig().then((cfg) => {
      setConfig(cfg);
      setIsLoading(false);
    });
  }, []);

  return { ...config, isLoading };
}
